import { mdiCheck } from '@mdi/js';
import { Button as AriaButton } from 'react-aria-menubutton';
import styled from 'styled-components';
import { focusVisibleWithBefore } from '../utils/focus-indicator';
import Icon from '../Icon';
import BaseSpinner from '../Spinner';
const selectFieldStyles = {
  lineHeightSmall: '1rem',
  lineHeight: '1.4rem',
  lineHeightLarge: '3rem'
};
const getBackgroundColor = ({
  'data-inline': inline,
  'data-opened': opened,
  theme
}) => {
  if (opened) return inline ? 'transparent' : theme.palette.backgroundPrimary;
  if (inline) return 'transparent';
  return theme.palette.backgroundPrimary;
};
const getBorderBottomColor = ({
  'data-inline': inline,
  theme
}) => inline ? theme.textInputBorder : '';
const getBorderColor = ({
  'data-error': error,
  'data-inline': inline,
  theme
}) => {
  if (inline) return 'none';
  if (error) return theme.textInputErrorBorder;
  return theme.textInputBorder;
};
const getSelectedValueHeight = ({
  theme,
  size,
  showLabel,
  showSelectedLabel
}) => {
  if (showLabel && showSelectedLabel && size === 'Small') {
    return '2.5rem';
  }
  return theme[`textInputHeight${size}`];
};
const getCursor = ({
  disabled,
  'data-loading': loading
}) => {
  if (disabled) return 'not-allowed';
  if (loading) return 'wait';
  return 'pointer';
};
export const Button = styled.div`
  height: ${({
  'data-size': size = '',
  theme,
  'data-multiple': multiple
}) => multiple ? 'auto' : theme[`textInputHeight${size}`]};
  min-height: ${({
  'data-size': size = '',
  theme,
  'data-multiple': multiple
}) => multiple ? theme[`textInputHeight${size}`] : 'auto'};
  background-color: ${props => getBackgroundColor(props)};
  border: none;
  border-radius: ${props => props.theme.textInputBorderRadius};
  width: 100%;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  position: relative;

  /* Multi-select specific styling */
  ${({
  'data-multiple': multiple
}) => multiple && `
    padding: 1.25rem 0 0.75rem 0;
    align-items: flex-start;
  `}

  &:focus {
    outline: none;
    border: ${props => props.theme.textInputFocusBorder};
    box-shadow: ${props => props.theme.focusBoxShadow};
  }

  &[aria-disabled='true'] {
    color: ${({
  theme
}) => theme.palette.contentDisabled};
    background-color: ${({
  theme
}) => theme.palette.backgroundStrong};
  }

  cursor: ${getCursor};
`;
export const Container = styled.div`
  border: ${props => getBorderColor(props)};
  border-bottom: ${props => getBorderBottomColor(props)};
  border-radius: ${props => props.theme.textInputBorderRadius};
  box-shadow: ${({
  'data-error': error,
  theme
}) => error ? `${theme.palette.semanticError} 0 0 0 1px` : 'none'};
`;
export const Label = styled.label`
  font-size: ${({
  'data-size': size = '',
  theme
}) => theme[`textInputLabel${size}FontSize`]};
  font-weight: ${({
  'data-size': size = '',
  theme
}) => theme[`textInputLabelFloating${size}FontWeight`]};
  color: ${({
  $disabled,
  theme
}) => $disabled ? theme.palette.contentDisabled : theme.palette.content};
  position: absolute;
  left: 1rem;
  top: ${props => props.theme.textInputTop};
  transform: translateY(-50%);
  cursor: text;
  transition: all 0.2s;
  white-space: nowrap;
  pointer-events: none;
`;
export const SelectedValue = styled.div`
  display: flex;
  align-items: center;
  font-size: ${({
  'data-size': size,
  theme
}) => theme[`textInputFontSize${size}`]};
  font-weight: ${({
  'data-size': size,
  theme
}) => theme[`textInputWeight${size}`]};
  padding: ${({
  'data-size': size,
  $showLabel,
  theme,
  $showSelectedLabel,
  'data-placeholder': placeholder
}) => {
  if (placeholder) {
    // Multi-select placeholder should match chips container padding + chip margin offset
    return '0rem 1rem 0rem 1rem';
  }
  return theme[`textInputPadding${$showLabel && $showSelectedLabel ? 'Floating' : ''}${size}`];
}};
  height: ${({
  'data-size': size = '',
  theme,
  $showLabel,
  $showSelectedLabel,
  'data-placeholder': placeholder
}) => {
  if (placeholder) {
    return '2.875rem'; // 46px - compact height for multi-select placeholder
  }
  return getSelectedValueHeight({
    theme,
    size,
    showLabel: $showLabel,
    showSelectedLabel: $showSelectedLabel
  });
}};
  line-height: ${({
  'data-size': size
}) => selectFieldStyles[`lineHeight${size}`]};
  color: ${props => props.theme.palette.content};

  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding-right: 1.8rem;

  & ~ label {
    ${({
  $showSelectedLabel
}) => $showSelectedLabel ? '' : 'display: none;'};
    font-size: ${({
  'data-size': size = '',
  theme
}) => theme[`textInputLabelFloating${size}FontSize`]};
    font-weight: ${({
  'data-size': size = '',
  theme
}) => theme[`textInputLabelFloating${size}FontWeight`]};
    color: ${props => props.theme.palette.content};
    top: ${props => props.theme.textInputTopFloating};
  }
`;
export const MenuItemCheckMark = styled(Icon).attrs({
  color: 'actionablePrimary',
  size: 'small',
  icon: mdiCheck
})`
  visibility: ${props => props.$isSelected ? 'visible' : 'hidden'};
`;
export const MenuItemCheckMarkWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
export const MenuItemContainer = styled.li`
  display: block;
  border-bottom: 1px solid ${props => props.theme.palette.border};
  color: ${props => props.theme.palette.content};
  cursor: pointer;
  margin: 0;

  &:hover {
    background-color: ${props => props.theme.palette.backgroundStrong};
  }

  &:active {
    background-color: ${props => props.theme.palette.actionableSecondary};
    color: ${props => props.theme.palette.contentReverse};
  }

  &:last-child {
    border-bottom: none;
  }

  ${focusVisibleWithBefore}
`;
const getTop = ({
  'data-direction': direction,
  'data-alignment': alignment
}) => {
  if (alignment === 'rightside' || alignment === 'leftside') {
    return direction === 'down' ? '0' : 'auto';
  }
  return direction === 'down' ? '100%' : 'auto';
};
const getBottom = ({
  'data-direction': direction,
  'data-alignment': alignment
}) => {
  if (alignment === 'rightside' || alignment === 'leftside') {
    return direction === 'down' ? 'auto' : '0';
  }
  return direction === 'down' ? 'auto' : '100%';
};
const RIGHT_BY_ALIGNMENT = {
  right: '0',
  left: 'auto',
  rightside: 'auto',
  leftside: '100%'
};
const LEFT_BY_ALIGNMENT = {
  right: 'auto',
  left: '0',
  rightside: '100%',
  leftside: 'auto'
};
const getMargin = ({
  'data-direction': direction,
  'data-alignment': alignment,
  'data-margin': margin
}) => {
  const usedMargin = margin || '0.2rem';
  if (alignment === 'rightside') {
    return `0 0 0 ${usedMargin}`;
  }
  if (alignment === 'leftside') {
    return `0 ${usedMargin} 0 0`;
  }
  return direction === 'down' ? `${usedMargin} 0 0 0` : `0 0 ${usedMargin} 0`;
};
export const MenuContainer = styled.ul`
  position: absolute;
  top: ${getTop};
  bottom: ${getBottom};
  right: ${({
  'data-alignment': alignment
}) => RIGHT_BY_ALIGNMENT[alignment]};
  left: ${({
  'data-alignment': alignment
}) => LEFT_BY_ALIGNMENT[alignment]};
  display: block;
  margin: ${getMargin};
  padding: 0;
  box-shadow: ${props => props.theme.boxShadowHigh};
  background-color: ${props => props.theme.palette.backgroundPrimary};
  border-radius: ${({
  theme
}) => theme.smallBorderRadius};
  z-index: ${props => props.theme.zIndex.dropdown};
  max-height: ${({
  'data-max-height': mh
}) => mh};
  overflow-y: auto;
  text-align: left;

  ${MenuItemContainer} {
    font-size: ${({
  'data-size': size
}) => size === 'small' ? '0.6875rem' : '1rem'};
    padding: ${({
  'data-size': size
}) => size === 'small' ? '0.3rem 1rem' : '0.8rem 1rem'};
  }
`;
export const Arrow = styled(Icon)`
  position: absolute;
  right: 0.5rem;
  top: calc(50% - 0.75rem);
  pointer-events: none;
  transition: transform 0.15s ease-in-out;

  @media (prefers-reduced-motion) {
    transition: none;
  }
`;
const arrowColorByType = {
  primary: 'contentReverse',
  secondary: 'actionablePrimary',
  neutral: 'contentWeak',
  danger: 'contentReverse'
};
const isBtnSmall = (y, n) => ({
  size
}) => size === 'small' || size === 'xsmall' ? y : n;
export const DropdownButton = styled(AriaButton)`
  position: relative;
  padding-left: ${isBtnSmall('0.75rem', '1.5rem')};
  padding-right: ${isBtnSmall('2rem', '3rem')};

  ${Arrow} {
    right: ${isBtnSmall('0', '0.5rem')};
    &:after {
      border-top-color: ${({
  type,
  theme
}) => theme.palette[arrowColorByType[type]]};
    }
  }

  cursor: ${getCursor};
`;
export const DisabledMenuItem = styled.div`
  color: ${props => props.theme.palette.contentDisabled};
  cursor: not-allowed;
`;
export const Spinner = styled(BaseSpinner)`
  position: absolute;
  left: unset;
  top: calc(50% - (${({
  height
}) => height} / 2));
  right: ${({
  dropdown,
  size
}) => dropdown && ['xsmall', 'small'].includes(size) ? '0.5rem' : '0.75rem'};
`;
export const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.375rem 1rem;
  max-width: calc(100% - 2.5rem); /* Account for arrow/icon */
  align-items: flex-start;
`;
export const SelectChip = styled.div`
  margin: 0.25rem 0.25rem 0.25rem 0;
  padding: 0;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;
export const SelectChipContent = styled.div`
  height: 1.75rem;
  min-width: auto;
  border-radius: 0.25rem;
  background-color: ${({
  theme
}) => theme.palette.backgroundStrong};
  border: 1px solid ${({
  theme
}) => theme.palette.border};
  padding: 0.25rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.125rem;
  transition: border-color 0.1s ease-in-out;

  ${SelectChip}:hover & {
    border-color: ${({
  theme
}) => theme.palette.borderStrongHover};
  }
`;
export const SelectChipText = styled.span`
  font-size: ${({
  theme
}) => theme.typography.sizes.text.desktop.sm.fontSize};
  line-height: 1.25rem;
  height: auto;
  font-weight: 500;
  display: flex;
  align-items: center;
`;
export const ChipRemoveIcon = styled(Icon)`
  cursor: pointer;
  transform: translate(2px, 2px);

  &:hover {
    opacity: 0.7;
  }
`;
//# sourceMappingURL=styles.js.map